import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  type CampaignDisplayStatus,
  type CampaignLifecycleStatus,
  getAdminCampaigns,
  type EntityListOfAdminCampaignDto,
  type SortDirection,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const ADMIN_CAMPAIGNS_QUERY_KEY = "ADMIN_CAMPAIGNS";

export type AdminCampaignsParams = {
  status?: CampaignLifecycleStatus;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  name?: string;
  code?: string;
  receiverCompanyName?: string;
  displayStatus?: CampaignDisplayStatus;
  startDateFrom?: string;
  endDateFrom?: string;
  dateCreatedFrom?: string;
  dateUpdatedFrom?: string;
};

export const adminCampaignsKeys = {
  all: [ADMIN_CAMPAIGNS_QUERY_KEY] as const,
  lists: () => [...adminCampaignsKeys.all, "list"] as const,
  list: (params?: AdminCampaignsParams) =>
    [...adminCampaignsKeys.lists(), params] as const,
  details: () => [...adminCampaignsKeys.all, "detail"] as const,
  detail: (id?: number | null) =>
    [...adminCampaignsKeys.details(), id] as const,
};

export const useAdminCampaignsQuery = (params: AdminCampaignsParams = {}) =>
  useQuery<EntityListOfAdminCampaignDto>({
    queryKey: adminCampaignsKeys.list(params),
    queryFn: processResponseWithApiProblemDetails(() =>
      getAdminCampaigns(
        params.status,
        params.name,
        params.code,
        params.receiverCompanyName,
        params.displayStatus,
        params.startDateFrom,
        params.endDateFrom,
        params.dateCreatedFrom,
        params.dateUpdatedFrom,
        params.offset,
        params.limit,
        params.sortBy,
        params.sortDirection,
      ),
    ),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 5 * MS_TIME.minute,
  });
